<template>
  <div class="mv">
    <div class="mv-head">
      <p class="mv-intro">
        A version combines one or more scans, each placed by its own transform.
        The <strong>current</strong> version is the one everyone sees in the 3D viewer and reports.
      </p>
      <button type="button" class="new-version-btn" @click="openCreate">
        <v-icon size="18">mdi-plus</v-icon>
        <span>New version</span>
      </button>
    </div>

    <ModelVersionTable ref="versionTable" :assetId="assetId" @changed="$emit('changed')" />

    <!-- New version dialog -->
    <v-dialog v-model="createDialog" max-width="560" persistent>
      <div class="modal">
        <header class="modal-head">
          <div class="modal-heading">
            <div class="modal-eyebrow">Model version</div>
            <h2 class="modal-title">New version</h2>
          </div>
          <button type="button" class="modal-close" aria-label="Close" @click="cancelScan">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </header>

        <div class="modal-body">
          <v-text-field v-model="versionName" label="Name" variant="outlined" density="comfortable"
            hide-details="auto" placeholder="e.g. V2 – north wing added" class="fld" />

          <v-text-field v-model="versionDescription" label="Description (optional)" variant="outlined"
            density="comfortable" hide-details="auto" placeholder="What changed in this version" class="fld" />

          <v-select v-model="selectedScanIds" :items="availableScans" item-title="name" item-value="id"
            label="Scans" multiple chips closable-chips variant="outlined" density="comfortable"
            hide-details="auto" class="fld" />
          <p v-if="!availableScans.length" class="fld-note">No processed scans are available to add yet.</p>

          <div v-if="selectedScanIds.length > 1" class="primary-block">
            <div class="primary-label">Primary scan</div>
            <div class="primary-hint">The base scan the others are positioned against (and the one the viewer renders).</div>
            <v-radio-group v-model="primaryScanId" density="comfortable" hide-details class="mt-1">
              <v-radio v-for="scan in selectedScans" :key="scan.id" :value="scan.id" :label="scan.name" color="#64ffda" />
            </v-radio-group>
          </div>

          <v-checkbox v-model="setToCurrent" :disabled="!selectedScanIds.length"
            label="Set as the current version" color="#64ffda" hide-details density="comfortable" class="mt-1" />
        </div>

        <footer class="modal-actions">
          <button type="button" class="btn-ghost" :disabled="isSaving" @click="cancelScan">Cancel</button>
          <button type="button" class="btn-primary" :disabled="!canCreate || isSaving" @click="saveVersionRecord">
            <v-progress-circular v-if="isSaving" indeterminate size="16" width="2" />
            <span v-else>Create version</span>
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import ModelVersionTable from '@/components/tables/ModelVersionTable.vue';
import CreateModelVersionModel from '@/domain/modelversion/CreateModelVersionModel';
import ScanAssociation from '@/domain/modelversion/ScanAssociation';
import ModelVersionService from '@/services/ModelVersionService';
import { AI_ANALYSIS_IN_PROGRESS, AWAITING_DUALLO_REVIEW, SCAN_PROCESSING_SUCCESS } from '@/utils/ScanStatus';
import { useAuth0 } from '@auth0/auth0-vue';

export default {
  name: 'AssetAdminViewModelVersions',
  components: { ModelVersionTable },
  props: {
    assetId: { type: String, default: null },
  },
  emits: ['changed'],
  setup() {
    const { user } = useAuth0();
    return { user };
  },
  data() {
    return {
      createDialog: false,
      isSaving: false,
      versionName: null,
      versionDescription: null,
      selectedScanIds: [],
      primaryScanId: null,
      setToCurrent: false,
      availableScans: [],
    };
  },
  computed: {
    selectedScans() {
      return this.availableScans.filter(scan => this.selectedScanIds.includes(scan.id));
    },
    canCreate() {
      return !!(this.versionName && this.versionName.trim()) && this.selectedScanIds.length >= 1;
    },
  },
  watch: {
    // Keep the primary valid: default to the first selected scan, re-point if it's deselected.
    selectedScanIds(ids) {
      if (!ids.includes(this.primaryScanId)) {
        this.primaryScanId = ids[0] ?? null;
      }
    },
  },
  async mounted() {
    await this.getAssetScans();
  },
  methods: {
    openCreate() {
      this.resetForm();
      this.createDialog = true;
    },
    async saveVersionRecord() {
      if (!this.canCreate) return;
      this.isSaving = true;
      try {
        // The primary scan is the base; the rest are supplementary, each starting with an identity
        // transform (positioning is adjusted later). Original scan records are untouched.
        const primaryScanId = this.primaryScanId ?? this.selectedScanIds[0] ?? null;
        const supplementaryScans = this.selectedScanIds
          .filter(scanId => scanId !== primaryScanId)
          .map(scanId => new ScanAssociation(scanId));
        const createdBy = this.user?.name || this.user?.email || 'Unknown';
        const versionRecord = new CreateModelVersionModel(
          this.assetId, this.versionName, this.versionDescription, primaryScanId,
          supplementaryScans, this.setToCurrent, createdBy);
        await ModelVersionService.createNewModelVersionRecord(versionRecord);
      } finally {
        await this.refreshTable();
        this.resetForm();
        this.createDialog = false;
        this.isSaving = false;
        this.$emit('changed');
      }
    },
    async getAssetScans() {
      // A scan is version-eligible once its model exists: fully processed, or still in the
      // AI-analysis / Duallo-review tail (the 3D model is complete by then).
      const scans = await ModelVersionService.getScansByAssetId(this.assetId);
      this.availableScans = scans.filter(scan =>
        scan.status === SCAN_PROCESSING_SUCCESS
        || scan.status === AI_ANALYSIS_IN_PROGRESS
        || scan.status === AWAITING_DUALLO_REVIEW);
    },
    resetForm() {
      this.versionName = null;
      this.versionDescription = null;
      this.selectedScanIds = [];
      this.primaryScanId = null;
      this.setToCurrent = false;
    },
    cancelScan() {
      this.resetForm();
      this.createDialog = false;
    },
    async refreshTable() {
      await this.$refs.versionTable.refreshTable();
    },
  },
};
</script>

<style scoped>
.mv { display: flex; flex-direction: column; gap: 16px; }

.mv-head {
  display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap;
}
.mv-intro { margin: 0; font-size: 0.9rem; color: #8aa0aa; line-height: 1.5; max-width: 640px; }
.mv-intro strong { color: #cfd8dc; font-weight: 700; }

.new-version-btn {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 11px 20px; border-radius: 10px;
  background: var(--duallo-accent-gradient); color: #071514;
  border: none; font: inherit; font-weight: 700; cursor: pointer;
  transition: filter 0.15s ease;
}
.new-version-btn:hover { filter: brightness(1.05); }

/* Dialog */
.modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: clamp(16px, calc(10px + 0.5vw), 22px);
  color: #fff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}
.modal-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.modal-eyebrow { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--duallo-accent); }
.modal-title { font-size: 1.3rem; font-weight: 700; margin: 3px 0 0; }
.modal-close {
  width: 34px; height: 34px; border-radius: 9px; background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08); color: #b0bec5; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
}
.modal-close:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }
.modal-body { display: flex; flex-direction: column; }
.fld { margin-bottom: 16px; }
.fld-note { margin: -8px 0 12px; font-size: 0.8rem; color: #f0b54a; }

.primary-block { margin: 4px 0 8px; }
.primary-label { font-size: 0.85rem; font-weight: 700; color: #cfd8dc; }
.primary-hint { font-size: 0.78rem; color: #8aa0aa; margin-top: 2px; }

.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; }
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 20px; border-radius: 10px; min-width: 140px;
  background: var(--duallo-accent-gradient); color: #071514;
  border: none; font: inherit; font-weight: 700; cursor: pointer;
}
.btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-ghost {
  padding: 10px 18px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer;
}
.btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
